import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Keychain from 'react-native-keychain';
import { normalizeEmail } from '../utils/normalizeEmail';

/**
 * Central place for sensitive credentials.
 *
 * Goals:
 * - Stop storing passwords/emails in AsyncStorage (legacy keys: ElAssist / PwAssist)
 * - Keep data in Keychain/Keystore instead
 * - Provide migration path so existing installs keep working
 */

const SERVICE_EMPLOYEE_PASSWORD = 'bizplus.employee.password.v1';
const SERVICE_EMPLOYEE_EMAIL = 'bizplus.employee.email.v1';

const LEGACY_EMAIL_KEY = 'ElAssist';
const LEGACY_PASSWORD_KEY = 'PwAssist';

type StoredString = { value: string };

async function setServiceValue(service: string, value: string) {
  // username is arbitrary; we store our value in the password field
  await Keychain.setGenericPassword('bizplus', value, { service });
}

async function getServiceValue(service: string): Promise<string | null> {
  const res = await Keychain.getGenericPassword({ service });
  if (!res) return null;
  const v = String(res.password || '').trim();
  return v ? v : null;
}

async function resetService(service: string) {
  try {
    await Keychain.resetGenericPassword({ service });
  } catch {
    // noop
  }
}

async function migrateLegacyAsyncStorageToKeychain() {
  const [legacyEmail, legacyPassword] = await Promise.all([
    AsyncStorage.getItem(LEGACY_EMAIL_KEY),
    AsyncStorage.getItem(LEGACY_PASSWORD_KEY),
  ]);

  const email = normalizeEmail(legacyEmail || '');
  const password = String(legacyPassword || '').trim();

  // Migrate only if we actually have values
  const tasks: Promise<any>[] = [];
  if (email) tasks.push(setEmployeeEmail(email));
  if (password) tasks.push(setEmployeePassword(password));
  if (tasks.length) {
    await Promise.all(tasks);
    await AsyncStorage.multiRemove([LEGACY_EMAIL_KEY, LEGACY_PASSWORD_KEY]);
  }
}

export async function setEmployeePassword(password: string) {
  const v = String(password || '').trim();
  if (!v) return;
  await setServiceValue(SERVICE_EMPLOYEE_PASSWORD, v);
  // remove legacy if present
  await AsyncStorage.removeItem(LEGACY_PASSWORD_KEY);
}

export async function getEmployeePassword(): Promise<string | null> {
  // Try keychain first
  const existing = await getServiceValue(SERVICE_EMPLOYEE_PASSWORD);
  if (existing) return existing;

  // migrate from legacy storage if present
  await migrateLegacyAsyncStorageToKeychain();
  return await getServiceValue(SERVICE_EMPLOYEE_PASSWORD);
}

export async function setEmployeeEmail(email: string) {
  const v = normalizeEmail(email);
  if (!v) return;
  await setServiceValue(SERVICE_EMPLOYEE_EMAIL, v);
  await AsyncStorage.removeItem(LEGACY_EMAIL_KEY);
}

export async function getEmployeeEmail(): Promise<string | null> {
  const existing = await getServiceValue(SERVICE_EMPLOYEE_EMAIL);
  if (existing) return existing;

  await migrateLegacyAsyncStorageToKeychain();
  return await getServiceValue(SERVICE_EMPLOYEE_EMAIL);
}

/**
 * Backoffice auto-login relies on having BOTH email + password.
 * We keep them independently and return only when both exist.
 */
export async function getBackofficeCredentials(): Promise<{
  email: string;
  password: string;
} | null> {
  const [email, password] = await Promise.all([
    getEmployeeEmail(),
    getEmployeePassword(),
  ]);
  if (!email || !password) return null;
  return { email, password };
}

export async function clearAllStoredCredentials() {
  await Promise.all([
    resetService(SERVICE_EMPLOYEE_EMAIL),
    resetService(SERVICE_EMPLOYEE_PASSWORD),
    AsyncStorage.multiRemove([LEGACY_EMAIL_KEY, LEGACY_PASSWORD_KEY]),
  ]);
}
